import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import LoadChatDrawer from '../components/LoadChatDrawer';
import { MessageSquare, ArrowRight, Truck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  assigned: 'bg-blue-100 text-blue-700',
  in_transit: 'bg-green-100 text-green-700',
  delivered: 'bg-slate-100 text-slate-600',
};

const ChatHub = () => {
  const { user } = useAuth();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatLoad, setChatLoad] = useState(null);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/loads`, { withCredentials: true });
        // Sort by most recent activity
        const sorted = [...res.data].sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
        setLoads(sorted);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = loads.filter(l => {
    if (filter === 'active') return l.status !== 'delivered';
    if (filter === 'delivered') return l.status === 'delivered';
    return true;
  });

  if (loading) return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="chat-hub-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Messages</h1>
          <p className="text-base text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {user?.role === 'driver' ? 'Chat with dispatch about your loads.' : 'Chat with drivers per load.'}
          </p>
        </div>

        <div className="flex items-center gap-2 mb-4">
          {['active', 'delivered', 'all'].map(f => (
            <button
              key={f}
              data-testid={`chat-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${filter === f ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {f === 'active' ? 'Active' : f === 'delivered' ? 'Delivered' : 'All'}
              <span className="ml-1.5 text-[10px] opacity-70">
                ({f === 'active' ? loads.filter(l => l.status !== 'delivered').length : f === 'delivered' ? loads.filter(l => l.status === 'delivered').length : loads.length})
              </span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center" data-testid="no-loads">
            <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No loads to chat about yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
            {filtered.map(l => (
              <button
                key={l.id}
                data-testid={`chat-row-${l.id}`}
                onClick={() => setChatLoad({ id: l.id, load_ref: l.load_ref || l.id.slice(-6) })}
                className="w-full text-left p-4 hover:bg-slate-50 transition-colors flex items-center gap-4"
              >
                <div className="w-10 h-10 bg-[#002FA7]/10 rounded-md flex items-center justify-center flex-shrink-0">
                  <Truck className="w-4 h-4 text-[#002FA7]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                      {l.load_ref || `#${l.id.slice(-6)}`}
                    </p>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STATUS_PILL[l.status] || 'bg-slate-100 text-slate-600'}`}>
                      {l.status.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {l.pickup_location} → {l.delivery_location}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-[#002FA7] text-xs font-semibold" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <MessageSquare className="w-3.5 h-3.5" /> Chat
                  <ArrowRight className="w-3 h-3" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <LoadChatDrawer
        loadId={chatLoad?.id}
        loadRef={chatLoad?.load_ref}
        open={!!chatLoad}
        onClose={() => setChatLoad(null)}
      />
    </DashboardLayout>
  );
};

export default ChatHub;
